import React, { useState, useRef, useEffect } from "react";
import "../Styles/MusicPlayer.css";
import {
  FaRandom,
  FaRedo,
  FaStepBackward,
  FaStepForward,
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaHeart,
} from "react-icons/fa";

// Import images
import fearlessCover from "../assets/fearless-cover.svg";
import mortalsCover from "../assets/mortals-cover.svg";
import skyHighCover from "../assets/sky-high-cover.svg";
import invincibleCover from "../assets/invincible-cover.svg";
import heroesTonightCover from "../assets/heroes-tonight-cover.svg";

const MusicPlayer = ({ darkMode }) => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isShuffled, setIsShuffled] = useState(false);
  const [isLooped, setIsLooped] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const audioRef = useRef(null);

  // Demo playlist with NCS (NoCopyrightSounds) tracks
  const playlist = [
    {
      id: 1,
      title: "Fearless pt. II",
      artist: "TULE, Chris Linton",
      album: "NCS Release",
      duration: "3:31",
      cover: fearlessCover, // Custom NCS style cover
      audioUrl:
        process.env.PUBLIC_URL +
        "/music/TULE, Chris Linton - Fearless pt. II (feat. Chris Linton) [NCS Release].mp3",
    },
    {
      id: 2,
      title: "Mortals",
      artist: "Warriyo feat. Laura Brehm",
      album: "NCS Release",
      duration: "3:49",
      cover: mortalsCover, // Custom NCS style cover
      audioUrl: process.env.PUBLIC_URL + "/music/warriyo-mortals.mp3",
    },
    {
      id: 3,
      title: "Elektronomia - Sky High",
      artist: "Elektronomia",
      album: "NCS Release",
      duration: "3:15",
      cover: skyHighCover, // Custom NCS style cover
      audioUrl: process.env.PUBLIC_URL + "/music/elektronomia-sky-high.mp3",
    },
    {
      id: 4,
      title: "Invincible",
      artist: "DEAF KEV feat. Invincible",
      album: "NCS Release",
      duration: "4:33",
      cover: invincibleCover, // Custom NCS style cover
      audioUrl: process.env.PUBLIC_URL + "/music/deaf-kev-invincible.mp3",
    },
    {
      id: 5,
      title: "Heroes Tonight",
      artist: "Janji feat. Johnning",
      album: "NCS Release",
      duration: "3:28",
      cover: heroesTonightCover, // Custom NCS style cover
      audioUrl: process.env.PUBLIC_URL + "/music/janji-heroes-tonight.mp3",
    },
  ];

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const nextTrack = () => {
    const next = (currentTrack + 1) % playlist.length;
    setCurrentTrack(next);
    setIsPlaying(false);
    setTimeout(() => {
      if (isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const previousTrack = () => {
    const prev = currentTrack === 0 ? playlist.length - 1 : currentTrack - 1;
    setCurrentTrack(prev);
    setIsPlaying(false);
    setTimeout(() => {
      if (isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const seekTime = (e.nativeEvent.offsetX / e.target.offsetWidth) * duration;
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const toggleShuffle = () => setIsShuffled(!isShuffled);
  const toggleLoop = () => setIsLooped(!isLooped);
  const toggleLike = () => setIsLiked(!isLiked);

  return (
    <div className={`music-player ${darkMode ? "" : "light-mode"}`}>
      <div className="music-header">
        <h2>Music</h2>
        <div className="music-controls-top">
          <button
            className={`control-btn ${isShuffled ? "active" : ""}`}
            onClick={toggleShuffle}
            title="Shuffle"
          >
            <FaRandom size={18} />
          </button>
          <button
            className={`control-btn ${isLooped ? "active" : ""}`}
            onClick={toggleLoop}
            title="Repeat"
          >
            <FaRedo size={18} />
          </button>
        </div>
      </div>

      <div className="music-content">
        <div className="album-art">
          <img
            src={playlist[currentTrack].cover}
            alt={playlist[currentTrack].album}
          />
        </div>

        <div className="player-controls-section">
          <div className="track-info">
            <h3 className="track-title">{playlist[currentTrack].title}</h3>
            <p className="track-artist">{playlist[currentTrack].artist}</p>
            <p className="track-album">{playlist[currentTrack].album}</p>
          </div>

          <div className="progress-container">
            <div className="progress-bar" onClick={handleSeek}>
              <div
                className="progress-fill"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              ></div>
            </div>
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="main-controls">
            <button className="control-btn large" onClick={previousTrack}>
              <FaStepBackward size={20} />
            </button>
            <button className="play-btn" onClick={togglePlay}>
              {isPlaying ? <FaPause size={24} /> : <FaPlay size={24} />}
            </button>
            <button className="control-btn large" onClick={nextTrack}>
              <FaStepForward size={20} />
            </button>
          </div>

          <div className="volume-control">
            <FaVolumeUp size={16} />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
          </div>

          <button
            className={`like-btn ${isLiked ? "liked" : ""}`}
            onClick={toggleLike}
          >
            <FaHeart size={18} />
          </button>
        </div>
      </div>

      <div className="playlist">
        <h4>Playlist ({playlist.length} tracks)</h4>
        <div className="playlist-tracks">
          {playlist.map((track, index) => (
            <div
              key={track.id}
              className={`playlist-track ${
                currentTrack === index ? "active" : ""
              }`}
              onClick={() => setCurrentTrack(index)}
            >
              <img
                src={track.cover}
                alt={track.album}
                className="track-thumb"
              />
              <div className="track-details">
                <span className="track-name">{track.title}</span>
                <span className="track-artist-small">{track.artist}</span>
              </div>
              <span className="track-duration">{track.duration}</span>
            </div>
          ))}
        </div>
      </div>

      <audio
        ref={audioRef}
        src={playlist[currentTrack].audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={nextTrack}
        onLoadedMetadata={handleTimeUpdate}
      />
    </div>
  );
};

export default MusicPlayer;
